// src/pages/BusinessQR/BusinessQR.js
import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Fab,
} from "@mui/material";
import {
  Image as ImageIcon,
  CameraAlt as CameraIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  QrCode as QrIcon,
  Link as LinkIcon,
} from "@mui/icons-material";
import QRCode from "qrcode";
import jsQR from "jsqr";

const BusinessQR = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [qrText, setQrText] = useState("");
  const [finalImage, setFinalImage] = useState(null);
  const [openScanner, setOpenScanner] = useState(false);
  const [scannedResult, setScannedResult] = useState("");
  const videoRef = useRef();
  const canvasRef = useRef();
  const [stream, setStream] = useState(null);

  const steps = [
    "Upload Your Image",
    "Enter Link or Text",
    "Generate QR Image",
    "Scan & Test",
  ];

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setUploadedImage(ev.target.result);
      setActiveStep(1);
    };
    reader.readAsDataURL(file);
  };

  const createQRLinkedImage = async () => {
    if (!uploadedImage || !qrText) {
      alert("Upload an image and enter link/text first!");
      return;
    }
    try {
      const qrCanvas = document.createElement("canvas");
      await QRCode.toCanvas(qrCanvas, qrText, { width: 150 });

      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        ctx.drawImage(qrCanvas, canvas.width - 170, canvas.height - 170, 150, 150);
        const combined = canvas.toDataURL("image/png");
        setFinalImage(combined);
        setActiveStep(3);

        // Auto download
        const link = document.createElement("a");
        link.href = combined;
        link.download = "business-qr.png";
        link.click();

        alert("✅ QR added successfully! Downloaded your business image.");
      };
      img.src = uploadedImage;
    } catch (err) {
      console.error(err);
      alert("❌ Failed to create QR linked image");
    }
  };

  const startScanner = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });
      setStream(s);
      setOpenScanner(true);
      if (videoRef.current) videoRef.current.srcObject = s;
    } catch (err) {
      alert("Camera access denied");
    }
  };

  const stopScanner = () => {
    if (stream) stream.getTracks().forEach((t) => t.stop());
    setStream(null);
    setOpenScanner(false);
  };

  const captureFrame = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const video = videoRef.current;
    const ctx = canvas.getContext("2d");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const code = jsQR(imgData.data, imgData.width, imgData.height);
    if (code) {
      stopScanner();
      setScannedResult(code.data);
    }
  };

  useEffect(() => {
    let scanInterval;
    if (openScanner) {
      scanInterval = setInterval(() => captureFrame(), 1000);
    }
    return () => clearInterval(scanInterval);
  }, [openScanner]);

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: "auto" }}>
      <Typography variant="h4" align="center" gutterBottom fontWeight={700}>
        💼 Business QR Generator
      </Typography>
      <Typography align="center" color="textSecondary" sx={{ mb: 3 }}>
        Upload your image → Enter any link or message → Get a QR-embedded image
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>

      {activeStep === 0 && (
        <Box textAlign="center">
          <Button
            variant="contained"
            startIcon={<ImageIcon />}
            component="label"
          >
            Upload Image
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={handleImageUpload}
            />
          </Button>
          {uploadedImage && (
            <Box mt={2}>
              <img
                src={uploadedImage}
                alt="preview"
                style={{ maxWidth: "100%", borderRadius: 8 }}
              />
              <Button sx={{ mt: 2 }} variant="contained" onClick={() => setActiveStep(1)}>
                Next
              </Button>
            </Box>
          )}
        </Box>
      )}

      {activeStep === 1 && (
        <Box textAlign="center">
          <Typography variant="h6" gutterBottom>
            Enter your business link or text
          </Typography>
          <input
            type="text"
            placeholder="https://yourbusiness.com or any message"
            value={qrText}
            onChange={(e) => setQrText(e.target.value)}
            style={{
              width: "80%",
              padding: "10px",
              marginTop: "10px",
              borderRadius: "8px",
              border: "1px solid #ccc",
            }}
          />
          <Box mt={2}>
            <Button
              variant="contained"
              startIcon={<LinkIcon />}
              onClick={() => setActiveStep(2)}
              disabled={!qrText}
            >
              Continue
            </Button>
          </Box>
        </Box>
      )}

      {activeStep === 2 && (
        <Box textAlign="center">
          <Typography gutterBottom>
            Add a QR code with your link/text onto your uploaded image.
          </Typography>
          <Button
            variant="contained"
            startIcon={<QrIcon />}
            onClick={createQRLinkedImage}
          >
            Generate Business QR Image
          </Button>
        </Box>
      )}

      {activeStep === 3 && finalImage && (
        <Box textAlign="center">
          <Typography>✅ QR added to your image!</Typography>
          <img
            src={finalImage}
            alt="QR linked"
            style={{ maxWidth: "100%", marginTop: 10, borderRadius: 8 }}
          />
          <Box mt={2}>
            <Button
              variant="contained"
              startIcon={<CameraIcon />}
              onClick={startScanner}
            >
              Test Scanner
            </Button>
          </Box>
        </Box>
      )}

      {/* SCANNER */}
      <Dialog open={openScanner} onClose={stopScanner} maxWidth="md" fullWidth>
        <DialogTitle>📷 Scan to Test Your QR</DialogTitle>
        <DialogContent>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            style={{ width: "100%", borderRadius: 8 }}
          />
          <canvas ref={canvasRef} style={{ display: "none" }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={stopScanner} startIcon={<DeleteIcon />}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* SCAN RESULT */}
      {scannedResult && (
        <Box
          mt={3}
          p={2}
          sx={{
            border: "1px solid #ccc",
            borderRadius: 2,
            textAlign: "center",
            background: "#fafafa",
          }}
        >
          <Typography variant="h6">🔍 Scanned Result</Typography>
          <Typography variant="body2" sx={{ mt: 1, wordBreak: "break-all" }}>
            {scannedResult}
          </Typography>
          {scannedResult.startsWith("http") && (
            <Button
              sx={{ mt: 1 }}
              variant="contained"
              color="primary"
              href={scannedResult}
              target="_blank"
            >
              Open Link
            </Button>
          )}
        </Box>
      )}

      {activeStep === 3 && (
        <Fab
          color="primary"
          aria-label="scan"
          onClick={startScanner}
          sx={{ position: "fixed", bottom: 24, right: 24 }}
        >
          <SearchIcon />
        </Fab>
      )}
    </Box>
  );
};

export default BusinessQR;
